"use client";

import * as React from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import Logo from "@/components/ui/Logo";
import { useNavigate } from "react-router-dom";
import {
  Home,
  Users,
  Shield,
  Settings,
  ChevronLeft,
  ChevronRight,
  LogOut,
  FolderOpen,
  Image,
  Layout,
  BookOpen,
  Menu,
  Sidebar as SidebarIcon,
  Film,
  UserCheck,
  Bot,
  MessageSquare,
  Monitor,
  TrendingUp,
  Drama,
  Clapperboard,
  Database,
  RefreshCw,
  Download,
} from "lucide-react";
import { canRead, PermissionSubject } from "@/utils/permissions";
import { ROUTES } from "@/constants/routes";

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
  onLogout: () => void;
  currentPath: string;
}

interface MenuItem {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  path: string;
  badge?: string;
}

// Menu items configuration with permissions
const menuItemsConfig: (MenuItem & { permission?: string })[] = [
  {
    icon: Home,
    label: "Dashboard",
    path: ROUTES.DASHBOARD,
    // Dashboard is always visible
  },
  {
    icon: Users,
    label: "Người dùng",
    path: ROUTES.USERS,
    permission: PermissionSubject.USER,
  },
  {
    icon: Shield,
    label: "Vai trò",
    path: ROUTES.ROLES,
    permission: PermissionSubject.ROLE,
  },
  {
    icon: FolderOpen,
    label: "Danh mục",
    path: ROUTES.CATEGORIES,
    permission: PermissionSubject.CATEGORY,
  },
  {
    icon: Film,
    label: "Danh sách phim",
    path: ROUTES.ARTICLES,
    permission: PermissionSubject.ARTICLE,
  },
  // {
  //   icon: ListChecks,
  //   label: "Phim bộ",
  //   path: ROUTES.SERIES,
  //   permission: PermissionSubject.ARTICLE,
  // },
  {
    icon: Image,
    label: "Hình ảnh",
    path: ROUTES.IMAGES,
    permission: PermissionSubject.IMAGE,
  },
  {
    icon: Layout,
    label: "Trang",
    path: ROUTES.PAGES,
    permission: PermissionSubject.PAGE,
  },
  {
    icon: BookOpen,
    label: "Blog Post",
    path: ROUTES.BLOG_POSTS,
    permission: PermissionSubject.BLOG_POST,
  },
  {
    icon: Menu,
    label: "Menu",
    path: ROUTES.MENUS,
    permission: PermissionSubject.MENU,
  },
  {
    icon: SidebarIcon,
    label: "Sidebar",
    path: ROUTES.SIDEBAR,
    permission: PermissionSubject.WIDGET,
  },
  {
    icon: TrendingUp,
    label: "Phim Thịnh Hành",
    path: ROUTES.TRENDING_MOVIES,
    permission: PermissionSubject.WIDGET,
  },
  // {
  //   icon: Film,
  //   label: "Danh sách phim",
  //   path: ROUTES.FILM_LIST,
  //   permission: PermissionSubject.FILM_LIST,
  // },
  {
    icon: UserCheck,
    label: "Tác giả",
    path: ROUTES.AUTHORS,
    permission: PermissionSubject.AUTHOR,
  },
  {
    icon: Drama,
    label: "Diễn viên",
    path: ROUTES.ACTORS,
    permission: PermissionSubject.ACTOR,
  },
  {
    icon: Clapperboard,
    label: "Đạo diễn",
    path: ROUTES.DIRECTORS,
    permission: PermissionSubject.DIRECTOR,
  },
  {
    icon: Bot,
    label: "Robot.txt",
    path: ROUTES.ROBOT_TXT,
    permission: PermissionSubject.ROBOT_TXT,
  },
  {
    icon: Monitor,
    label: "Quản lý thông tin",
    path: ROUTES.INFORMATION,
    permission: PermissionSubject.PAGE,
  },
  {
    icon: Monitor,
    label: "Chuyển hướng",
    path: ROUTES.REDIRECTIONS,
    permission: PermissionSubject.PAGE,
  },
  {
    icon: Monitor,
    label: "Chỗ quảng cáo",
    path: ROUTES.AD_SLOTS,
    permission: PermissionSubject.BANNER,
  },
  {
    icon: MessageSquare,
    label: "Bình luận",
    path: ROUTES.COMMENTS,
    permission: PermissionSubject.COMMENT,
  },
  {
    icon: Database,
    label: "KKMovie",
    path: ROUTES.KKMOVIES,
    permission: PermissionSubject.KKMOVIE,
  },
  {
    icon: RefreshCw,
    label: "KKPhim Sync",
    path: ROUTES.KKPHIM_SYNC,
    permission: PermissionSubject.KKPHIM_SYNC,
  },
  {
    icon: RefreshCw,
    label: "OPhim Sync",
    path: ROUTES.OPHIM_SYNC,
    permission: PermissionSubject.OPHIM_SYNC,
  },
  {
    icon: Download,
    label: "Import Phim",
    path: ROUTES.MOVIE_IMPORT,
    permission: PermissionSubject.ARTICLE,
  },
  {
    icon: Database,
    label: "Quản lý Cache",
    path: ROUTES.CACHE,
    permission: PermissionSubject.INFORMATION,
  },
  {
    icon: Settings,
    label: "Cài đặt",
    path: ROUTES.SETTINGS,
    // Settings might be visible to all or require specific permission
  },
];

// Filter menu items based on user permissions
const getFilteredMenuItems = (): MenuItem[] => {
  return menuItemsConfig.filter((item) => {
    // Dashboard is always visible
    if (item.path === ROUTES.DASHBOARD) {
      return true;
    }

    // If no permission required, show the item
    if (!item.permission) {
      return true;
    }

    // Check if user has read permission for this subject
    return canRead(item.permission);
  });
};

export function Sidebar({
  isCollapsed,
  onToggle,
  onLogout,
  currentPath,
}: SidebarProps) {
  const navigate = useNavigate();
  return (
    <div
      className={cn(
        "relative flex h-full flex-col bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 transition-all duration-300 ease-in-out",
        isCollapsed ? "w-16" : "w-64"
      )}
    >
      {/* Header */}
      <div className="flex !h-16 items-center justify-between px-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-3 !h-[61px]">
          {!isCollapsed && (
            <>
              <Logo size="sm" />
              <span className="text-lg font-bold text-gray-900 dark:text-white transition-all duration-200 truncate">
                Hệ thống quản trị
              </span>
            </>
          )}
        </div>

        <Button
          variant="ghost"
          size="sm"
          onClick={onToggle}
          className="h-8 w-8 p-0 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        >
          {isCollapsed ? (
            <ChevronRight className="h-4 w-4 text-gray-600 dark:text-gray-400" />
          ) : (
            <ChevronLeft className="h-4 w-4 text-gray-600 dark:text-gray-400" />
          )}
        </Button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 p-3 overflow-y-auto scrollbar-hide">
        {getFilteredMenuItems().map((item) => {
          const isActive = currentPath === item.path;
          const Icon = item.icon;

          return (
            <div key={item.path} className="relative group">
              <Button
                variant="ghost"
                onClick={() => {
                  navigate(item.path);
                }}
                className={cn(
                  "w-full justify-start h-11 px-3 text-left transition-all duration-200",
                  "hover:bg-gray-50 dark:hover:bg-gray-800 hover:shadow-sm cursor-pointer",
                  isActive
                    ? "bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg"
                    : "text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white",
                  isCollapsed ? "justify-center px-0" : ""
                )}
              >
                <Icon
                  className={cn(
                    "h-5 w-5 transition-transform duration-200",
                    isActive
                      ? "text-white"
                      : "text-gray-600 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-gray-300",
                    isCollapsed ? "" : "mr-3"
                  )}
                />

                {!isCollapsed && (
                  <>
                    <span className="font-medium truncate">{item.label}</span>
                    {item.badge && (
                      <span className="ml-auto px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full animate-pulse">
                        {item.badge}
                      </span>
                    )}
                  </>
                )}
              </Button>

              {/* Tooltip for collapsed state */}
              {isCollapsed && (
                <div className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-2 py-1 bg-gray-900 text-white text-sm rounded-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-50">
                  {item.label}
                  {item.badge && (
                    <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-500 rounded-full">
                      {item.badge}
                    </span>
                  )}
                </div>
              )}

              {/* Active indicator */}
              {isActive && (
                <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-1 h-8 bg-gradient-to-b from-blue-600 to-blue-700 rounded-r-full animate-pulse" />
              )}
            </div>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="border-t border-gray-200 p-3">
        <Button
          variant="ghost"
          onClick={onLogout}
          className={cn(
            "w-full justify-start h-11 px-3 text-red-600 hover:text-red-700 hover:bg-red-50 transition-all duration-200 cursor-pointer",
            isCollapsed ? "justify-center px-0" : ""
          )}
        >
          <LogOut
            className={cn(
              "h-5 w-5 transition-transform duration-200",
              isCollapsed ? "" : "mr-3"
            )}
          />
          {!isCollapsed && <span className="font-medium">Đăng xuất</span>}
        </Button>
      </div>
    </div>
  );
}
